import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, Compass } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const TourContext = createContext(null);
export const useTour = () => useContext(TourContext);

// Each step navigates to `path`, then highlights the first matching selector in `target`.
// If no target is found, the step is skipped automatically so the tour never gets stuck.
const STEPS = [
  {
    path: "/dashboard",
    target: ['[data-testid="kpi-active-projects"]'],
    title: "Willkommen bei PrüfDoc Pro",
    text: "Dieser kurze Rundgang zeigt Ihnen die wichtigsten Bereiche. Das Dashboard bündelt Ihre Kennzahlen: aktive Projekte, offene Tickets und Mängel. Ein Klick auf eine Kachel führt direkt in den passenden Bereich.",
  },
  {
    path: "/aufgaben",
    target: ['[data-testid="my-tasks-page"]'],
    title: "Meine Aufgaben",
    text: "Hier sehen Sie alle Mängel, Tickets und Prüfungen, die Ihnen zugewiesen sind. Leitung und Admin sehen unter „Teamaufgaben“ die Aufgaben aller Mitarbeiter und können sie neu zuweisen.",
  },
  {
    path: "/projects",
    target: ['[data-testid="new-project-btn"]', '[data-testid="projects-page"]'],
    title: "Projekte anlegen & bearbeiten",
    text: "Mit „Neues Projekt“ legen Sie eine Einrichtung an. Über den Stift auf der Projektkarte oder „Bearbeiten“ im Projekt ändern Sie später Adresse, Bauherr und alle anderen Projektdaten.",
  },
  {
    path: "/projects",
    target: ['[data-testid^="project-card-"]', '[data-testid="projects-page"]'],
    title: "Fortschritt & Offline-Stand",
    text: "Jede Karte zeigt Tickets und Mängel abgearbeitet. Darunter sehen Sie, ob das Projekt offline verfügbar ist und wie viele Mängel, Tickets und Fotos auf diesem Gerät liegen („12 von 14 Fotos“). Ein Tipp auf fehlende Fotos lädt sie gezielt nach.",
  },
  {
    path: "/overview/plans",
    target: ['[data-testid="overview-page"]'],
    title: "Baupläne & Marker",
    text: "Im Bauplan setzen Sie Positionen. Mit dem Schalter „Verschieben“ lassen sich Marker per Ziehen versetzen (mit „Rückgängig“), „Alle Schilder“ stellt die Schildgröße für den ganzen Plan ein. Im Positionsfenster erfassen Sie Mängel, Tickets und Hinweise (Info/Achtung/Warnung).",
  },
  {
    path: "/overview/tickets",
    target: ['[data-testid="overview-page"]'],
    title: "Tickets",
    text: "Alle Tickets projektübergreifend. Mit „Neues Ticket erstellen“ wählen Sie Projekt, Bauplan und Position – oder legen das Ticket ohne Position an – und hängen direkt Fotos an.",
  },
  {
    path: "/overview/defects",
    target: ['[data-testid="overview-page"]'],
    title: "Mängel",
    text: "Alle Mängel nach Projekten gruppiert. Ein Tipp öffnet den Mangel direkt im Bauplan. Von hier erstellen Sie den Mängelbericht als PDF oder den kompakten Mängelbericht TEXT.",
  },
  {
    path: "/overview/photos",
    target: ['[data-testid="photo-report-start"]', '[data-testid="overview-page"]'],
    title: "Fotos & Fotobericht",
    text: "In der Großansicht blättern Sie mit den Pfeilen (oder Pfeiltasten). Über „Fotobericht“ wählen Sie Fotos aus und drucken sie auf DIN A4 – wahlweise mit Beschriftung und Deckblatt.",
  },
  {
    path: "/pruefungen",
    target: ['[data-testid="inspections-page"]'],
    title: "Prüfungen & Wartungen",
    text: "Alle fälligen und überfälligen Prüfungen. Ergebnis eintragen, Nachweise anhängen – bei wiederkehrenden Prüfungen wird die nächste Fälligkeit automatisch berechnet.",
  },
  {
    path: "/loeschanfragen",
    target: ['[data-testid="deletion-requests-page"]'],
    title: "Anfragen",
    text: "Löschanfragen der Haustechnik mit Vorher- und Nachher-Foto. Administrator, Geschäftsleitung und Leitung bestätigen oder lehnen hier ab.",
  },
  {
    path: "/overview/reports",
    target: ['[data-testid="overview-page"]'],
    title: "Berichte & PDF-Export",
    text: "Erzeugen Sie eine fertige Dokumentation als PDF – mit Plänen, Mängeln, Hinweisen und Fotos. Bauplan-Ausschnitt und Fotos werden dabei automatisch passend auf der Seite verteilt.",
  },
  {
    path: "/dashboard",
    target: ['[data-testid="nav-mehr"]', '[data-testid="mobilenav-mehr"]'],
    title: "Menü „Mehr“",
    text: "Baupläne, Mengen, Berichte und Dokumente finden Sie unter „Mehr“. Im Profilmenü unter „Menü einstellen“ legen Sie selbst fest, was direkt in der Leiste steht und in welcher Reihenfolge.",
  },
  {
    path: "/benutzer",
    requiredRole: "admin",
    target: ['[data-testid="user-management-page"]'],
    title: "Benutzerverwaltung",
    text: "Als Administrator laden Sie hier Team-Mitglieder ein, vergeben Rollen und Rechte und verwalten Zugänge. Neue Google-Anmeldungen geben Sie unter „Zugangsanfragen“ frei.",
  },
  {
    path: "/dashboard",
    target: ['[data-testid="user-menu-trigger"]'],
    title: "Profilmenü",
    text: "Hier finden Sie Hilfe, „Menü einstellen“, „Offline aktualisieren“, „Aktualisieren“ und diesen Rundgang zum Wiederholen. Viel Erfolg mit PrüfDoc Pro!",
  },
];

const PAD = 8;

function computeTooltipPos(rect) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const tipW = Math.min(340, vw - 24);
  const tipH = 210; // estimate for placement decision
  let top, left, placement;
  const below = rect.top + rect.height + PAD + tipH + 12 < vh;
  if (below) {
    top = rect.top + rect.height + PAD + 12;
    placement = "bottom";
  } else if (rect.top - PAD - tipH - 12 > 0) {
    top = rect.top - PAD - tipH - 12;
    placement = "top";
  } else {
    top = Math.max(12, vh / 2 - tipH / 2);
    placement = "center";
  }
  left = rect.left + rect.width / 2 - tipW / 2;
  left = Math.max(12, Math.min(left, vw - tipW - 12));
  return { top, left, width: tipW, placement };
}

export function TourProvider({ children }) {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [running, setRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const dirRef = useRef(1);
  const elRef = useRef(null);
  const autoRef = useRef(false);
  const navAttemptsRef = useRef(0);

  // Same tour for everyone, but steps whose route a role cannot reach are removed
  // up front (deterministic) so a guarded redirect can never trap the tour.
  const steps = useMemo(
    () => STEPS.filter((s) => !s.requiredRole || (user && user.role === s.requiredRole)),
    [user]
  );
  const total = steps.length;
  const clampedIndex = Math.min(stepIndex, Math.max(0, total - 1));
  const step = steps[clampedIndex];

  const markSeen = useCallback(async () => {
    if (user && user.has_seen_tour === false) {
      try {
        await api.post("/auth/tour-seen");
        setUser({ ...user, has_seen_tour: true });
      } catch (e) {
        /* non-blocking */
      }
    }
  }, [user, setUser]);

  const startTour = useCallback(() => {
    dirRef.current = 1;
    navAttemptsRef.current = 0;
    setRect(null);
    setStepIndex(0);
    setRunning(true);
  }, []);

  const finish = useCallback(() => {
    setRunning(false);
    setRect(null);
    setStepIndex(0);
    navAttemptsRef.current = 0;
    elRef.current = null;
    markSeen();
  }, [markSeen]);

  const goto = useCallback((dir) => {
    dirRef.current = dir;
    navAttemptsRef.current = 0;
    setRect(null);
    elRef.current = null;
    setStepIndex((i) => {
      const nextI = i + dir;
      if (nextI < 0) return 0;
      return nextI;
    });
  }, []);

  const next = useCallback(() => {
    if (clampedIndex + 1 >= total) {
      finish();
    } else {
      goto(1);
    }
  }, [clampedIndex, total, finish, goto]);

  const back = useCallback(() => {
    if (clampedIndex > 0) goto(-1);
  }, [clampedIndex, goto]);

  // Auto-start once for a brand new account.
  useEffect(() => {
    if (user && user.has_seen_tour === false && !autoRef.current && !running) {
      autoRef.current = true;
      const t = setTimeout(() => startTour(), 900);
      return () => clearTimeout(t);
    }
  }, [user, running, startTour]);

  // Locate/measure the target for the current step; navigate first if needed.
  useEffect(() => {
    if (!running || !step) return;

    const skipInDir = () => {
      navAttemptsRef.current = 0;
      setRect(null);
      elRef.current = null;
      if (dirRef.current > 0) {
        if (clampedIndex + 1 >= total) finish();
        else setStepIndex(clampedIndex + 1);
      } else if (clampedIndex > 0) {
        setStepIndex(clampedIndex - 1);
      } else {
        finish();
      }
    };

    // Wrong route: navigate there, but give up if the route is unreachable
    // (e.g. a role guard keeps redirecting) so the tour never loops forever.
    if (location.pathname !== step.path) {
      navAttemptsRef.current += 1;
      if (navAttemptsRef.current > 6) {
        skipInDir();
        return;
      }
      navigate(step.path);
      return; // effect re-runs on pathname change
    }

    let cancelled = false;
    let tries = 0;
    const measure = (el) => {
      navAttemptsRef.current = 0;
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    const tick = () => {
      if (cancelled) return;
      let el = null;
      for (const sel of step.target) {
        el = document.querySelector(sel);
        if (el) break;
      }
      if (el) {
        elRef.current = el;
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        setTimeout(() => {
          if (!cancelled) measure(el);
        }, 260);
        return;
      }
      tries += 1;
      if (tries > 18) {
        skipInDir(); // target missing (e.g. empty data) -> skip
        return;
      }
      setTimeout(tick, 90);
    };
    tick();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, clampedIndex, location.pathname, step]);

  // Keep the highlight aligned on scroll/resize.
  useEffect(() => {
    if (!running || !rect) return;
    const remeasure = () => {
      if (elRef.current) {
        const r = elRef.current.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener("resize", remeasure);
    window.addEventListener("scroll", remeasure, true);
    return () => {
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("scroll", remeasure, true);
    };
  }, [running, rect]);

  // Escape to end the tour.
  useEffect(() => {
    if (!running) return;
    const onKey = (e) => {
      if (e.key === "Escape") finish();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [running, finish, next, back]);

  const tip = rect ? computeTooltipPos(rect) : null;

  return (
    <TourContext.Provider value={{ startTour, running }}>
      {children}
      {running && rect && tip && step && (
        <div data-testid="tour-overlay" className="fixed inset-0 z-[9998]" aria-live="polite">
          {/* click blocker */}
          <div className="absolute inset-0" style={{ cursor: "default" }} onClick={(e) => e.stopPropagation()} />
          {/* spotlight */}
          <div
            className="absolute rounded-xl transition-all duration-300 pointer-events-none"
            style={{
              top: rect.top - PAD,
              left: rect.left - PAD,
              width: rect.width + PAD * 2,
              height: rect.height + PAD * 2,
              boxShadow: "0 0 0 9999px rgba(15,23,42,0.55)",
              outline: "2px solid rgba(217,119,6,0.9)",
            }}
          />
          {/* tooltip */}
          <div
            data-testid="tour-tooltip"
            className="absolute z-[10000] bg-white rounded-2xl shadow-2xl border border-slate-200 p-5 animate-in fade-in zoom-in-95 duration-200"
            style={{ top: tip.top, left: tip.left, width: tip.width }}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Compass className="h-4 w-4 text-primary" />
                </div>
                <h3 className="font-head font-extrabold text-slate-900 text-base leading-tight">{step.title}</h3>
              </div>
              <button
                onClick={finish}
                data-testid="tour-skip-btn"
                title="Rundgang beenden"
                className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">{step.text}</p>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
              <span className="text-xs font-medium text-slate-400" data-testid="tour-progress">
                Schritt {clampedIndex + 1} von {total}
              </span>
              <div className="flex items-center gap-2">
                {clampedIndex > 0 && (
                  <button
                    onClick={back}
                    data-testid="tour-back-btn"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100"
                  >
                    <ChevronLeft className="h-4 w-4" />Zurück
                  </button>
                )}
                <button
                  onClick={next}
                  data-testid="tour-next-btn"
                  className="inline-flex items-center gap-1 px-4 py-1.5 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 transition-colors"
                >
                  {clampedIndex + 1 >= total ? "Fertig" : "Weiter"}
                  {clampedIndex + 1 < total && <ChevronRight className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </TourContext.Provider>
  );
}
